import classNames from 'classnames';
import { Formik } from 'formik';
import { useToggle } from 'react-use';
import * as Yup from 'yup';

import { useTrackAddedShareLinkPasscode } from '@air/analytics';
import { type ShortUrlResponse } from '@air/api/types';
import { EyeClosed as EyeClosedIcon, Eye as EyeIcon } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { IconButton } from '@air/primitive-icon-button';
import { Input } from '@air/primitive-input';

import { type ShareUrlResponseWithSource } from '~/components/SharePrivateClipModal/types';
import { UpsellFlashIconButton } from '~/components/SharePrivateClipModalV2/UpsellFlashIconButton';
import { SharePrivateContentPermissionToggle } from '~/components/SharePrivateContentModal/shared/SharePrivateContentPermissionToggle';
import {
  PASSCODE_PROTECTION_INPUT,
  PASSCODE_PROTECTION_LABEL,
  PASSCODE_PROTECTION_SUBMIT_BUTTON,
  PASSCODE_PROTECTION_TOGGLE,
} from '~/constants/testIDs';
import { SHARE_MODAL_UPSELL_MESSAGE } from '~/constants/upsells';
import { useShareSettings } from '~/hooks/useShareSettings';
import { PASSWORD_REQS_STRING } from '~/utils/Auth';

const SignUpValidationSchema = Yup.object().shape({
  passcode: Yup.string()
    .required('Passcode is required')
    .min(8, 'Passcode must be at least 8 characters.')
    .matches(/\d+/, 'Passcode must include a number.')
    .matches(/[a-z]/, 'Passcode must include one lowercase letter.')
    .matches(/[A-Z]/, 'Passcode must include one uppercase letter.')
    .default(''),
});

type SharePrivateContentPasscodeProtectionProps = {
  shortIdInfo: ShortUrlResponse;
  onChangePasscode: (passcode: string | null) => Promise<ShareUrlResponseWithSource>;
};

export const SharePrivateContentPasscodeProtection = ({
  shortIdInfo,
  onChangePasscode,
}: SharePrivateContentPasscodeProtectionProps) => {
  const { passcodeEnabled } = useShareSettings();
  const [showPasscode, toggleShowPasscode] = useToggle(true);
  const [isShowingPasscodeForm, showPasscodeForm] = useToggle(!!shortIdInfo.passcode);
  const { trackAddedShareLinkPasscode } = useTrackAddedShareLinkPasscode();

  return (
    <div>
      <SharePrivateContentPermissionToggle
        label="Add passcode protection"
        id="password-protect"
        checked={isShowingPasscodeForm}
        dataTestId={PASSCODE_PROTECTION_LABEL}
        labelTestId={PASSCODE_PROTECTION_TOGGLE}
        isLoading={false}
        disabled={!passcodeEnabled}
        onCheckedChange={(checked) => {
          if (shortIdInfo.passcode && isShowingPasscodeForm) {
            onChangePasscode(null);
          }
          showPasscodeForm(checked);
        }}
      >
        {!passcodeEnabled && <UpsellFlashIconButton>{SHARE_MODAL_UPSELL_MESSAGE}</UpsellFlashIconButton>}
      </SharePrivateContentPermissionToggle>

      {passcodeEnabled && isShowingPasscodeForm && (
        <Formik
          validationSchema={SignUpValidationSchema}
          initialValues={{
            passcode: shortIdInfo.passcode ?? '',
          }}
          onSubmit={async ({ passcode }, { setSubmitting, resetForm }) => {
            try {
              setSubmitting(true);
              trackAddedShareLinkPasscode({ shortId: shortIdInfo?.id });
              await onChangePasscode(passcode || null);
              setSubmitting(false);

              resetForm({
                values: {
                  passcode,
                },
              });
            } catch (_error) {
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting, handleSubmit, values, handleChange, dirty, isValid, errors, touched, handleBlur }) => (
            <div>
              <form className="relative mt-3 flex items-center" onSubmit={handleSubmit}>
                <div className="relative w-full">
                  <Input
                    size="large"
                    value={values.passcode}
                    data-testid={PASSCODE_PROTECTION_INPUT}
                    onChange={handleChange}
                    type={showPasscode ? 'text' : 'password'}
                    name="passcode"
                    className={classNames('w-full pr-8', !isValid && dirty && 'border-red-9')}
                    placeholder="Enter passcode"
                    onBlur={handleBlur}
                  />
                  <IconButton
                    appearance="ghost"
                    className="absolute top-1 right-1"
                    color="grey"
                    label="Show passcode"
                    icon={showPasscode ? EyeIcon : EyeClosedIcon}
                    onClick={toggleShowPasscode}
                    size="medium"
                  />
                </div>
                <Button
                  appearance="filled"
                  className="ml-3"
                  color="grey"
                  data-testid={PASSCODE_PROTECTION_SUBMIT_BUTTON}
                  disabled={isSubmitting || !dirty || !isValid}
                  isLoading={isSubmitting}
                  size="large"
                  type="submit"
                >
                  Save
                </Button>
              </form>
              {!!errors.passcode && touched.passcode ? (
                <p className="mb-0 text-12 text-red-9">{errors.passcode}</p>
              ) : (
                <p className="mb-0 text-12 text-grey-10">{PASSWORD_REQS_STRING}</p>
              )}
            </div>
          )}
        </Formik>
      )}
    </div>
  );
};
